import { Bell, CheckCircle2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/Button";
import { Card, CardBody } from "@/shared/ui/Card";
import { EmptyState } from "@/shared/ui/EmptyState";
import { HazardLevel } from "@/shared/ui/HazardLevel";
import { Separator } from "@/shared/ui/Separator";
import { StatusDot } from "@/shared/ui/StatusDot";
import { STATE_LABEL_KEY } from "./alertMeta";
import type { Alert, AlertLevel, AlertState } from "@/shared/api/models";

/**
 * DetailPanel — the selected-alert (detail) column. Extracted from AlertsPage
 * so the AlarmCenter overlay can present the same master-detail layout. Ack /
 * clear are lifted to the parent via `onAck` / `onClear` (both receive the
 * alert id) so each host wires its own mutations + toasts.
 */
export function DetailPanel({
  alert,
  onAck,
  onClear,
  canAck = true,
  canClear = true,
}: {
  alert: Alert | null;
  onAck: (id: number) => void;
  onClear: (id: number) => void;
  /** RBAC — hide (not disable) the control when the user can't perform it. */
  canAck?: boolean;
  canClear?: boolean;
}) {
  const { t } = useTranslation();
  if (!alert) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            Icon={Bell}
            title={t("widgets.alerts.detailPanel.emptyTitle")}
            description={t("widgets.alerts.detailPanel.emptyDesc")}
          />
        </CardBody>
      </Card>
    );
  }
  const isOpen = alert.state === "open";
  const isCleared = alert.state === "cleared";
  return (
    <Card>
      <CardBody className="space-y-4">
        <div>
          <HazardLevel level={(alert.level ?? 0) as AlertLevel} ri={alert.ri_at_open} size="lg" />
          <h2 className="mt-3 text-[18px] font-semibold tracking-[-0.012em]">
            {alert.gas || t("widgets.alerts.detailPanel.compositeGas")} · {t(STATE_LABEL_KEY[alert.state as AlertState])}
          </h2>
          {alert.message && (
            <p className="mt-1 text-[13.5px] text-[var(--fg-subtle)] whitespace-pre-line">
              {alert.message}
            </p>
          )}
        </div>

        <Separator />

        <dl className="grid grid-cols-2 gap-y-2 text-[12.5px]">
          <Kv label={t("widgets.alerts.detailPanel.kv.raisedAt")} value={new Date(alert.raised_at!).toLocaleString("ko-KR")} />
          <Kv label={t("widgets.alerts.detailPanel.kv.acknowledgedAt")} value={alert.acknowledged_at ? new Date(alert.acknowledged_at).toLocaleString("ko-KR") : "—"} />
          <Kv label={t("widgets.alerts.detailPanel.kv.clearedAt")} value={alert.cleared_at ? new Date(alert.cleared_at).toLocaleString("ko-KR") : "—"} />
          <Kv label={t("widgets.alerts.detailPanel.kv.acknowledgedBy")}     value={String(alert.acknowledged_by ?? "—")} />
        </dl>

        <Separator />

        <DeliverySection delivery={alert.delivery} />

        <Separator />

        <div className="flex items-center gap-2">
          {canAck && (
            <Button
              intent="primary"
              size="md"
              disabled={!isOpen}
              onClick={() => onAck(alert.id!)}
            >
              <CheckCircle2 size={15} strokeWidth={1.85} className="mr-1.5" />
              {t("widgets.alerts.detailPanel.ack")}
            </Button>
          )}
          {canClear && (
            <Button
              intent="outline"
              size="md"
              disabled={isCleared}
              onClick={() => onClear(alert.id!)}
            >
              <XCircle size={15} strokeWidth={1.85} className="mr-1.5" />
              {t("widgets.alerts.detailPanel.clear")}
            </Button>
          )}
          <span className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-[var(--fg-subtle)]">
            <StatusDot kind={isOpen ? "critical" : isCleared ? "ok" : "caution"} size={7} />
            <span>{t(STATE_LABEL_KEY[alert.state as AlertState])}</span>
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

/* ─── delivery ────────────────────────────────────────────────────── */

// `label` holds an i18n key; resolved with t() at the render site.
const DELIVERY_CHANNELS: { key: string; label: string }[] = [
  { key: "in_app", label: "widgets.alerts.detailPanel.channel.in_app" },
  { key: "push", label: "widgets.alerts.detailPanel.channel.push" },
  { key: "sms", label: "widgets.alerts.detailPanel.channel.sms" },
  { key: "relay", label: "widgets.alerts.detailPanel.channel.relay" },
];
const DELIVERY_STATUS: Record<
  string,
  { label: string; dot: "ok" | "caution" | "offline" | "critical" }
> = {
  sent: { label: "widgets.alerts.detailPanel.delivery.sent", dot: "ok" },
  skipped: { label: "widgets.alerts.detailPanel.delivery.skipped", dot: "caution" },
  unconfigured: { label: "widgets.alerts.detailPanel.delivery.unconfigured", dot: "offline" },
  failed: { label: "widgets.alerts.detailPanel.delivery.failed", dot: "critical" },
};

/** Per-channel alert fan-out status, read off `Alert.delivery`. */
function DeliverySection({ delivery }: { delivery: unknown }) {
  const { t } = useTranslation();
  const d = (delivery ?? {}) as Record<
    string,
    { status?: string; detail?: string }
  >;
  return (
    <div>
      <div className="mb-1.5 text-[12px] font-semibold text-[var(--fg-muted)]">
        {t("widgets.alerts.detailPanel.deliveryTitle")}
      </div>
      <ul className="space-y-1.5">
        {DELIVERY_CHANNELS.map((ch) => {
          const rec = d[ch.key];
          const st = rec?.status ? DELIVERY_STATUS[rec.status] : null;
          return (
            <li key={ch.key} className="flex items-center gap-2 text-[12px]">
              <StatusDot kind={st?.dot ?? "offline"} size={7} />
              <span className="w-[64px] shrink-0 text-[var(--fg)]">{t(ch.label)}</span>
              <span className="min-w-0 truncate text-[var(--fg-subtle)]">
                {st ? t(st.label) : "—"}
                {rec?.detail && (
                  <span className="ml-1.5 text-[11px]">· {rec.detail}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-[var(--fg-subtle)]">{label}</dt>
      <dd className="text-[var(--fg)] tabular-nums text-right">{value}</dd>
    </>
  );
}
